import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ExportHistoryPanel = ({ className = '' }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');

  const exportHistory = [
    {
      id: 1,
      name: "products_best_price_2025-10-17.xlsx",
      type: "best_price",
      format: "xlsx",
      size: "2.4 MB",
      generatedAt: "2025-10-17 14:30:15",
      generatedBy: "John Smith",
      downloadCount: 3,
      status: "completed",
      expiresAt: "2025-11-17 14:30:15"
    },
    {
      id: 2,
      name: "all_groups_weekly_2025-10-17.csv",
      type: "all_groups",
      format: "csv",
      size: "8.7 MB",
      generatedAt: "2025-10-17 09:00:00",
      generatedBy: "System (Scheduled)",
      downloadCount: 1,
      status: "completed",
      expiresAt: "2025-11-17 09:00:00"
    },
    {
      id: 3,
      name: "ambiguous_matches_review_2025-10-16.json",
      type: "ambiguous",
      format: "json",
      size: "1.2 MB",
      generatedAt: "2025-10-16 16:45:22",
      generatedBy: "Sarah Johnson",
      downloadCount: 0,
      status: "completed",
      expiresAt: "2025-11-16 16:45:22"
    },
    {
      id: 4,
      name: "unmatched_products_2025-10-16.csv",
      type: "unmatched",
      format: "csv",
      size: "456 KB",
      generatedAt: "2025-10-16 11:20:08",
      generatedBy: "Mike Davis",
      downloadCount: 2,
      status: "completed",
      expiresAt: "2025-11-16 11:20:08"
    },
    {
      id: 5,
      name: "supplier_analysis_2025-10-15.xlsx",
      type: "all_groups",
      format: "xlsx",
      size: "5.1 MB",
      generatedAt: "2025-10-15 13:15:30",
      generatedBy: "Lisa Chen",
      downloadCount: 5,
      status: "expired",
      expiresAt: "2025-10-15 13:15:30"
    }
  ];

  const typeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'best_price', label: 'Best Price Reports' },
    { value: 'all_groups', label: 'All Product Groups' },
    { value: 'ambiguous', label: 'Ambiguous Matches' },
    { value: 'unmatched', label: 'Unmatched Products' }
  ];

  const sortOptions = [
    { value: 'date_desc', label: 'Newest First' },
    { value: 'date_asc', label: 'Oldest First' },
    { value: 'name_asc', label: 'Name A-Z' },
    { value: 'size_desc', label: 'Largest First' },
    { value: 'downloads_desc', label: 'Most Downloaded' }
  ];

  const getTypeIcon = (type) => {
    switch (type) {
      case 'best_price': return 'TrendingDown';
      case 'all_groups': return 'Grid3X3';
      case 'ambiguous': return 'AlertTriangle';
      case 'unmatched': return 'FileX';
      default: return 'FileText';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center space-x-1 text-xs bg-success bg-opacity-10 text-success px-2 py-1 rounded">
            <Icon name="CheckCircle" size={10} />
            <span>Available</span>
          </span>
        );
      case 'expired':
        return (
          <span className="inline-flex items-center space-x-1 text-xs bg-error bg-opacity-10 text-error px-2 py-1 rounded">
            <Icon name="Clock" size={10} />
            <span>Expired</span>
          </span>
        );
      default:
        return null;
    }
  };

  const handleDownload = (exportItem) => {
    if (exportItem?.status === 'completed') {
      console.log('Downloading:', exportItem?.name);
      // Simulate download
    }
  };

  const handleDelete = (exportId) => {
    console.log('Deleting export:', exportId);
  };

  const filteredHistory = exportHistory?.filter(item => {
    const matchesSearch = item?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         item?.generatedBy?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesType = filterType === 'all' || item?.type === filterType;
    return matchesSearch && matchesType;
  });

  return (
    <div className={`bg-card border border-border rounded-lg ${className}`}>
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Export History</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Download previous reports and manage export files
            </p>
          </div>
          <Button
            variant="outline"
            iconName="RefreshCw"
            iconPosition="left"
          >
            Refresh
          </Button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            placeholder="Search exports..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            iconName="Search"
          />
          <Select
            options={typeOptions}
            value={filterType}
            onChange={setFilterType}
            placeholder="Filter by type"
          />
          <Select
            options={sortOptions}
            value={sortBy}
            onChange={setSortBy}
            placeholder="Sort by"
          />
        </div>
      </div>
      {/* Export List */}
      <div className="p-6">
        {filteredHistory?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Export History</h3>
            <p className="text-muted-foreground">
              {searchTerm || filterType !== 'all' ?'No exports match your current filters' :'Generate your first report to see it here'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredHistory?.map((exportItem) => (
              <div key={exportItem?.id} className="border border-border rounded-lg p-4 hover:bg-muted hover:bg-opacity-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    <div className="w-10 h-10 bg-primary bg-opacity-10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name={getTypeIcon(exportItem?.type)} size={18} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="font-medium text-foreground truncate">{exportItem?.name}</h4>
                        {getStatusBadge(exportItem?.status)}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-sm text-muted-foreground">
                        <div>
                          <span className="font-medium">Size:</span> {exportItem?.size}
                        </div>
                        <div>
                          <span className="font-medium">Generated:</span> {new Date(exportItem.generatedAt)?.toLocaleDateString()}
                        </div>
                        <div>
                          <span className="font-medium">By:</span> {exportItem?.generatedBy}
                        </div>
                        <div>
                          <span className="font-medium">Downloads:</span> {exportItem?.downloadCount}
                        </div>
                      </div>
                      {exportItem?.status === 'completed' && (
                        <div className="text-xs text-muted-foreground mt-1">
                          Expires: {new Date(exportItem.expiresAt)?.toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
                    {exportItem?.status === 'completed' ? (
                      <>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleDownload(exportItem)}
                          iconName="Download"
                          iconPosition="left"
                        >
                          Download
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(exportItem?.id)}
                          iconName="Trash2"
                          className="text-error hover:text-error"
                          title="Delete export"
                        />
                      </>
                    ) : (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled
                          iconName="Clock"
                          iconPosition="left"
                        >
                          Expired
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(exportItem?.id)}
                          iconName="Trash2"
                          className="text-error hover:text-error"
                          title="Delete export"
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Footer */}
      {filteredHistory?.length > 0 && (
        <div className="p-6 border-t border-border bg-muted bg-opacity-50">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              Showing {filteredHistory?.length} of {exportHistory?.length} exports
            </span>
            <div className="flex items-center space-x-4">
              <span>Total storage used: 18.2 MB</span>
              <Button
                variant="ghost"
                size="sm"
                iconName="Trash2"
                iconPosition="left"
                className="text-error hover:text-error"
              >
                Clear Expired
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportHistoryPanel;